import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuth } from '../lib/contexts/AuthContext';
import { formatCurrency, formatNumber, copyToClipboard } from '../lib/utils';
import StatCard from '../components/ui/StatCard';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { Wallet, TrendingUp, Users, ArrowUpCircle, Layers, Zap, Copy, Check, Share2, Link2 } from 'lucide-react';

export default function DashboardPage() {
  const { user } = useAuth();
  const [copied, setCopied] = useState('');
  const { data: walletSummary } = useQuery({ queryKey: ['walletSummary'], queryFn: () => api.get('/wallet/summary').then(r => r.data) });
  const { data: investments } = useQuery({ queryKey: ['investments'], queryFn: () => api.get('/investments').then(r => r.data) });
  const { data: teamStats } = useQuery({ queryKey: ['teamStats'], queryFn: () => api.get('/mlm/stats').then(r => r.data) });
  const { data: levelIncome } = useQuery({ queryKey: ['levelIncome'], queryFn: () => api.get('/investments/level-income/me').then(r => r.data) });

  const wallet = walletSummary?.wallets?.[0];
  const summary = walletSummary?.summary;
  const activeInvestments = investments?.filter((i: any) => i.status === 'ACTIVE') || [];
  const totalRoi = investments?.reduce((sum: number, i: any) => sum + parseFloat(i.totalEarned || '0'), 0) || 0;
  const totalLevel = levelIncome?.reduce((s: number, l: any) => s + parseFloat(l.paidAmount || '0'), 0) || 0;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  const referralLink = `${window.location.origin}/register?ref=${user?.referralCode || ''}`;

  const handleCopy = async (text: string, label: string) => {
    await copyToClipboard(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="d-page-header">
        <h1 className="text-2xl font-bold text-white">{greeting}</h1>
        <p className="text-slate-400 mt-1">Here's your account overview.</p>
      </div>

      {/* Referral Link */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center shrink-0">
                <Link2 className="w-5 h-5 text-primary-400" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-400">Your Referral Link</p>
                <p className="text-sm font-mono text-white truncate">{referralLink}</p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => handleCopy(referralLink, 'link')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-500/10 border border-primary-500/20 rounded-lg text-xs font-medium text-primary-400 hover:bg-primary-500/20 transition-colors">
                {copied === 'link' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied === 'link' ? 'Copied!' : 'Copy Link'}
              </button>
              <button onClick={() => handleCopy(user?.referralCode || '', 'code')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] border border-white/10 rounded-lg text-xs font-medium text-slate-300 hover:bg-white/[0.08] transition-colors">
                {copied === 'code' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied === 'code' ? 'Copied!' : `Code: ${user?.referralCode}`}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Available Balance" value={formatCurrency(wallet?.balance || 0)} icon={Wallet} />
        <StatCard title="Daily ROI Income" value={formatCurrency(totalRoi)} icon={TrendingUp} changeType="positive" change="From investments" />
        <StatCard title="Level Income" value={formatCurrency(totalLevel)} icon={Layers} changeType="positive" change="From team" />
        <StatCard title="Total Withdrawals" value={formatCurrency(summary?.totalWithdrawals || 0)} icon={ArrowUpCircle} />
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Investments */}
        <Card>
          <CardHeader>
            <h2 className="text-base font-semibold text-white">My Investments</h2>
          </CardHeader>
          <CardContent>
            {activeInvestments.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No active investments</p>
            ) : (
              <div className="space-y-3">
                {activeInvestments.slice(0, 5).map((inv: any) => (
                  <div key={inv.id} className="flex items-center justify-between p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-600/10 flex items-center justify-center">
                        <TrendingUp className="w-4 h-4 text-primary-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{inv.plan?.name}</p>
                        <p className="text-xs text-slate-500">{formatCurrency(inv.principalAmount)} invested</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-emerald-400">{formatCurrency(inv.totalEarned)}</p>
                      <p className="text-xs text-slate-500">{inv.daysCompleted} days</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {activeInvestments.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/[0.04]">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Total Invested</p>
                    <p className="text-sm font-bold text-white">{formatCurrency(activeInvestments.reduce((s: number, i: any) => s + parseFloat(i.principalAmount || '0'), 0))}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Earned</p>
                    <p className="text-sm font-bold text-emerald-400">{formatCurrency(activeInvestments.reduce((s: number, i: any) => s + parseFloat(i.totalEarned || '0'), 0))}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Active Plans</p>
                    <p className="text-sm font-bold text-white">{activeInvestments.length}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Overview */}
        <Card>
          <CardHeader>
            <h2 className="text-base font-semibold text-white">Team Overview</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Team Size', value: formatNumber(teamStats?.totalTeamSize || 0), icon: Users },
                { label: 'Active Members', value: formatNumber(teamStats?.activeTeam || 0), icon: Users },
                { label: 'Direct Referrals', value: formatNumber(teamStats?.directReferrals || 0), icon: Zap },
                { label: 'Active Directs', value: formatNumber(teamStats?.activeDirects || 0), icon: Zap },
              ].map((item) => (
                <div key={item.label} className="p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.04]">
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="w-3.5 h-3.5 text-slate-500" />
                    <p className="text-xs text-slate-400">{item.label}</p>
                  </div>
                  <p className="text-xl font-bold text-white">{item.value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <h2 className="text-base font-semibold text-white">Recent Transactions</h2>
        </CardHeader>
        <CardContent>
          {walletSummary?.transactions?.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No transactions yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="d-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Description</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {walletSummary?.transactions?.slice(0, 10).map((tx: any) => (
                    <tr key={tx.id}>
                      <td><Badge status={tx.type} /></td>
                      <td className="text-sm font-medium text-white">{formatCurrency(tx.amount)}</td>
                      <td className="text-sm text-slate-400">{tx.description || '-'}</td>
                      <td className="text-sm text-slate-400">{new Date(tx.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
